import { useState, type RefObject } from 'react';
import { useAllocate, useRespec, type CompanionDto, type StatBlock } from '../api/companions';
import { useWallet } from '../api/wallet';
import { ApiClientError } from '../api/client';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { ShellGlyph } from '../shell/glyphs';

export type AllocateSheetProps = {
  companion: CompanionDto;
  onClose: () => void;
  /** The button that opened this sheet — see BottomSheet.tsx's own doc comment on why this is
   *  needed for reliable focus-return. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
  /** Gallery-only escape hatch (same spirit as `portalContainer` above): pre-sets the respec
   *  footer's own inline confirm state so the gallery can show it as a static entry, the same way
   *  a real player would only reach it after tapping "Reset choices" — no other caller passes
   *  this. */
  initialConfirmingRespec?: boolean;
  /** Gallery-only — opens the header "?" legend (`allocate-help`) as a static entry. */
  initialShowingHelp?: boolean;
  /** Same seam `EquipmentSheet`/`LooksSheet`/`CareTray` already expose. This sheet never had one,
   *  so it fell to BottomSheet's `'sheet'` default (bottom drawer + drag handle) while every other
   *  habitat dock menu was opened with `variant="overlay"` — the stat menu was the odd one out by
   *  omission, not by design. */
  variant?: 'sheet' | 'overlay';
};

export const RESPEC_COST_SHELLS = 250;

const STATS: { key: keyof StatBlock; label: string; blurb: string }[] = [
  { key: 'vitality', label: 'Vitality', blurb: 'A bigger health pool in battle.' },
  { key: 'power', label: 'Power', blurb: 'Physical attacks hit harder.' },
  { key: 'focus', label: 'Focus', blurb: 'Elemental abilities hit harder.' },
  { key: 'guard', label: 'Guard', blurb: 'Physical hits hurt less — they still land.' },
  { key: 'ward', label: 'Ward', blurb: 'Elemental hits hurt less — they still land.' },
  { key: 'speed', label: 'Speed', blurb: 'Act earlier in a round, and dodge a little more often.' },
  { key: 'fortune', label: 'Fortune', blurb: 'Rarer finds when foraging or winning battles.' },
];

const GENERIC_ERROR = 'Something went wrong — please try again.';

function friendlyAllocateError(err: unknown): string {
  if (err instanceof ApiClientError && err.code === 'validation') return 'That allocation is no longer valid — try again.';
  return GENERIC_ERROR;
}

/**
 * SCREENS.md /habitat "allocate-sheet"/"respec-row": 7 stat rows with +/- steppers staged against
 * a local `pending` budget (never exceeding `companion.freePoints`, per stat capped only by that
 * shared budget — the server's own `StatService.allocate` has no per-stat ceiling either, unlike
 * Training's stat cap of 100), a remaining-points count, and Confirm — which fires ONE
 * `useAllocate` call per staged stat (task-21-brief.md: "simpler and transactional-per-stat;
 * acceptable"), awaited sequentially so a mid-batch rejection never races the next call against a
 * stale `freePoints` read. Each stat's own pending count is cleared the moment ITS call settles
 * (not all at once at the end) so the displayed value — `companion.stats[key]` (already bumped by
 * that same call's own optimistic cache write) plus whatever's still locally staged — never
 * double-counts an already-applied point.
 *
 * The respec footer row is a separate, currency-gated action (`useRespec`, never optimistic —
 * costs real Shells) with its own plain inline confirm state, independent of whatever's currently
 * staged in the steppers above.
 *
 * The header "?" (`allocate-help`) swaps the steppers for a seven-row legend rather than
 * stacking copy under them — the overlay's no-scroll budget (DESIGN.md §14) does not have
 * room for both at once. Copy names only shipped effects (battle HP / damage / mitigation /
 * turn-order+dodge / forage-and-battle drop rarity), not the unimplemented spec leftovers
 * (care-buff duration, minigame score multipliers, restock luck).
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3).
 */
export function AllocateSheet({
  companion, onClose, restoreFocusRef, portalContainer, initialConfirmingRespec = false,
  initialShowingHelp = false,
  variant = 'sheet',
}: AllocateSheetProps) {
  const allocate = useAllocate(companion.id);
  const respec = useRespec(companion.id);
  const wallet = useWallet();

  const [pending, setPending] = useState<Partial<Record<keyof StatBlock, number>>>({});
  const [allocateError, setAllocateError] = useState<string | null>(null);
  const [confirmingRespec, setConfirmingRespec] = useState(initialConfirmingRespec);
  const [respecError, setRespecError] = useState<string | null>(null);
  const [showingHelp, setShowingHelp] = useState(initialShowingHelp);

  const totalPending = Object.values(pending).reduce((sum, n) => sum + (n ?? 0), 0);
  const remaining = companion.freePoints - totalPending;

  function inc(stat: keyof StatBlock) {
    if (remaining <= 0) return;
    setPending((prev) => ({ ...prev, [stat]: (prev[stat] ?? 0) + 1 }));
  }
  function dec(stat: keyof StatBlock) {
    setPending((prev) => {
      const current = prev[stat] ?? 0;
      if (current <= 0) return prev;
      const next = { ...prev, [stat]: current - 1 };
      if (next[stat] === 0) delete next[stat];
      return next;
    });
  }

  async function handleConfirm() {
    if (allocate.isPending) return;
    setAllocateError(null);
    const staged = STATS.filter(({ key }) => (pending[key] ?? 0) > 0);
    for (const { key } of staged) {
      const points = pending[key] as number;
      try {
        await allocate.mutateAsync({ stat: key, points });
        setPending((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } catch (err) {
        setAllocateError(friendlyAllocateError(err));
        return; // Stop here — any still-staged stats stay staged for a retry.
      }
    }
    onClose();
  }

  async function handleRespec() {
    if (respec.isPending) return;
    setRespecError(null);
    try {
      await respec.mutateAsync();
      setPending({});
      setConfirmingRespec(false);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'insufficient_funds') {
        wallet.refetch();
        return;
      }
      setRespecError(GENERIC_ERROR);
    }
  }

  const walletShells = wallet.data?.shells;
  const respecShortfall = walletShells != null ? RESPEC_COST_SHELLS - walletShells : 0;
  const respecInsufficientReason = walletShells != null && respecShortfall > 0
    ? `You need ${respecShortfall} more Shells` : null;

  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="allocate-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
      variant={variant}
    >
      <div className="sheet-header">
        <div className="allocate-title-row">
          <Dialog.Title asChild>
            <h2>Allocate points</h2>
          </Dialog.Title>
          <button
            type="button"
            className="allocate-stat-help-btn"
            aria-label="What each stat does"
            aria-expanded={showingHelp}
            aria-controls="allocate-stat-help"
            onClick={() => setShowingHelp((open) => !open)}
          >
            ?
          </button>
        </div>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close allocate sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Spend {companion.name}&rsquo;s free points across seven stats, or reset previous choices for {RESPEC_COST_SHELLS} Shells.
      </Dialog.Description>

      <div className="sheet-body">
        {showingHelp ? (
          <dl id="allocate-stat-help" className="allocate-stat-help-list">
            {STATS.map(({ key, label, blurb }) => (
              <div key={key} className="allocate-stat-help-row">
                <dt>{label}</dt>
                <dd>{blurb}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <>
            <p className="allocate-remaining">{remaining} point{remaining === 1 ? '' : 's'} remaining</p>

            <ul className="allocate-stat-list">
              {STATS.map(({ key, label }) => {
                const staged = pending[key] ?? 0;
                const value = companion.stats[key] + staged;
                return (
                  <li key={key} className="allocate-stat-row">
                    <span className="allocate-stat-name">{label}</span>
                    <span className="allocate-stat-value">{value}</span>
                    <div className="allocate-stepper">
                      <button
                        type="button"
                        className="btn-quiet allocate-stepper-btn"
                        aria-label={`Decrease staged ${label} points`}
                        disabled={staged === 0 || allocate.isPending}
                        onClick={() => dec(key)}
                      >
                        −
                      </button>
                      <span className="allocate-stepper-count" aria-live="polite">{staged}</span>
                      <button
                        type="button"
                        className="btn-quiet allocate-stepper-btn"
                        aria-label={`Increase staged ${label} points`}
                        disabled={remaining <= 0 || allocate.isPending}
                        onClick={() => inc(key)}
                      >
                        +
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {allocateError && <p className="field-error" role="alert">{allocateError}</p>}

            <button
              type="button"
              className="btn-primary"
              disabled={totalPending === 0 || allocate.isPending}
              onClick={handleConfirm}
            >
              {allocate.isPending ? 'Saving…' : 'Confirm'}
            </button>

            <div className="allocate-respec-row">
              {!confirmingRespec ? (
                <button type="button" className="btn-quiet" onClick={() => { setRespecError(null); setConfirmingRespec(true); }}>
                  Reset choices — {RESPEC_COST_SHELLS} Shells
                </button>
              ) : (
                <div className="allocate-respec-confirm">
                  <p>Sure? This refunds your chosen points for {RESPEC_COST_SHELLS} Shells</p>
                  {respecInsufficientReason && <p className="field-error" role="alert">{respecInsufficientReason}</p>}
                  <div className="allocate-respec-actions">
                    <button type="button" className="btn-quiet" disabled={respec.isPending} onClick={() => setConfirmingRespec(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={respec.isPending || respecInsufficientReason != null}
                      onClick={handleRespec}
                    >
                      {respec.isPending ? (
                        'Resetting…'
                      ) : (
                        <>
                          <ShellGlyph /> Confirm
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {respecError && <p className="field-error" role="alert">{respecError}</p>}
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
